
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Upload } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  processExcelFile,
  processCsvFile,
  validateFileData,
  importStaffToSupabase
} from "./utils/staffFileProcessor";
import { ImportDialogContent } from "./components/ImportDialogContent";

interface ImportStaffDialogProps {
  onImportComplete: () => void;
}

export const ImportStaffDialog = ({
  onImportComplete,
}: ImportStaffDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any[]>([]);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasError(false);
    setErrorMessage("");
    setPreview([]);
    
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    
    try {
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
      let data: any[];
      
      if (fileExtension === 'csv') {
        data = await processCsvFile(selectedFile);
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        data = await processExcelFile(selectedFile);
      } else {
        throw new Error("Unsupported file format. Please upload CSV or Excel files (xlsx, xls).");
      }

      const validation = validateFileData(data);
      if (!validation.isValid) {
        setHasError(true);
        setErrorMessage(validation.error || "Invalid file format");
        return;
      }

      setPreview(data.slice(0, 5));
    } catch (error) {
      setHasError(true);
      setErrorMessage(error.message);
    }
  };

  const handleImport = async () => {
    if (!file || hasError) return;
    
    setIsLoading(true);
    
    try {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const data = fileExtension === 'csv' 
        ? await processCsvFile(file)
        : await processExcelFile(file);

      const result = await importStaffToSupabase(data);

      if (result.success) {
        toast({
          title: "Import completed",
          description: `Successfully imported ${data.length} staff`,
        });
        setIsOpen(false);
        setFile(null);
        setPreview([]);
        onImportComplete();
      } else {
        toast({
          title: "Import partially completed",
          description: `Some records could not be imported. ${result.error}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error importing staff:', error);
      toast({
        title: "Import failed",
        description: `Error: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setIsOpen(true)}
        className="gap-2"
      >
        <Upload className="h-4 w-4" />
        Import Staff
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <ImportDialogContent
          file={file}
          preview={preview}
          hasError={hasError}
          errorMessage={errorMessage}
          isLoading={isLoading}
          onFileChange={handleFileChange}
          onImport={handleImport}
          onClose={() => setIsOpen(false)}
        />
      </Dialog>
    </>
  );
};
